/**
 * voice-v8 - SVG Icon System (Pre.264).
 *
 * Single Source of Truth fuer alle Icons im UI. Keine Unicode-Pfeile mehr
 * (↗ × ‹ ›) — die sehen je nach Font anders aus und sind nicht stylebar.
 *
 * Usage: const ext = icon("external", { size: 14 }); el.appendChild(ext);
 *
 * Icons sind Lucide-inspired, optisch konsistent. currentColor → erbt Farbe.
 */

export type IconName =
  | "external" // Pfeil aus Box raus (für "öffnet im neuen Tab")
  | "close" // X
  | "chevron-left" // <
  | "chevron-right" // >
  | "chevron-up" //
  | "chevron-down" //
  | "info"
  | "image"
  | "search"
  | "check"
  | "x" // Synonym für close (kompakter)
  | "more" // ... horizontal
  | "link"
  | "map-pin" // Karten-Pin (Route/Maps, Pre.790 #60)
  | "alert" // Warn-Dreieck (Banner, Pre.800)
  | "arrow-right" // pfeil nach rechts, gerade
  | "download"
  | "mail" // Briefumschlag (Mail-Cards)
  | "bell" // Glocke (Erinnerungs-Cards, Ticket 279)
  | "plane"
  | "calendar"
  | "clock"
  | "activity"
  | "music"
  | "trash"
  | "pause"
  | "play"
  | "sun"
  | "cloud"
  | "cloud-rain"
  | "cloud-sun"
  | "cloud-snow"
  | "cloud-lightning";

interface IconOptions {
  size?: number; // Standard 16
  stroke?: number; // Standard 2
  className?: string;
  ariaLabel?: string;
}

const PATHS: Record<IconName, string> = {
  // Lucide: external-link
  external:
    "M15 3h6v6 M10 14L21 3 M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6",
  // Lucide: x
  close: "M18 6L6 18 M6 6l12 12",
  x: "M18 6L6 18 M6 6l12 12",
  "chevron-left": "M15 18l-6-6 6-6",
  "chevron-right": "M9 18l6-6-6-6",
  "chevron-up": "M18 15l-6-6-6 6",
  "chevron-down": "M6 9l6 6 6-6",
  info: "M12 16v-4 M12 8h.01",
  // Lucide: image
  image:
    "M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm5 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4 M21 15l-5-5L5 21",
  // Lucide: search
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm10 2l-4.35-4.35",
  // Lucide: check
  check: "M20 6L9 17l-5-5",
  bell: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M10.3 21a1.94 1.94 0 0 0 3.4 0",
  more: "M5 12h.01 M12 12h.01 M19 12h.01",
  // Lucide: link
  link: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07L11.75 5.18 M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
  // Lucide: map-pin (Pre.790 #60)
  "map-pin": "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z",
  // Lucide: triangle-alert (Pre.800)
  alert:
    "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z M12 9v4 M12 17h.01",
  // Lucide: arrow-right
  "arrow-right": "M5 12h14 M12 5l7 7-7 7",
  // Lucide: download
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3",
  // Lucide: mail
  mail: "M22 6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2z M2 7l10 6 10-6",
  // Lucide: plane / calendar / clock / activity / music (Glocke-Typ-Icons)
  plane:
    "M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z",
  calendar:
    "M8 2v4 M16 2v4 M3 10h18 M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2",
  activity: "M22 12h-4l-3 9L9 3l-3 9H2",
  music:
    "M9 18V5l12-2v13 M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0z M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z",
  // Lucide: trash
  trash:
    "M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6",
  // Lucide: pause / play
  pause: "M10 4v16 M14 4v16",
  play: "M7 4v16l13-8z",
  // Wetter (Lucide-Stil, feine Linie) — eigene Icon-Sprache fuer Karten
  sun: "M12 2v2 M12 20v2 M4.9 4.9l1.4 1.4 M17.7 17.7l1.4 1.4 M2 12h2 M20 12h2 M6.3 17.7l-1.4 1.4 M19.1 4.9l-1.4 1.4",
  cloud: "M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z",
  "cloud-rain": "M4 14.9A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.24 M16 14v6 M8 14v6 M12 16v6",
  "cloud-sun": "M12 2v2 M5.2 5.2l1.4 1.4 M2 12h2 M18.8 5.2l-1.4 1.4 M15.6 8.6A4 4 0 1 0 9.5 12 M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z",
  "cloud-snow": "M4 14.9A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.24 M8 15h.01 M8 19h.01 M12 17h.01 M12 21h.01 M16 15h.01 M16 19h.01",
  "cloud-lightning": "M6 16.3A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 .5 8.97 M13 12l-3 5h4l-3 5",
};

// Icons die einen "<circle>"-Teil brauchen (info hat einen Kreis ausen rum)
const CIRCLE_ICONS: Record<string, { cx: number; cy: number; r: number }> = {
  info: { cx: 12, cy: 12, r: 10 },
  "map-pin": { cx: 12, cy: 10, r: 3 },
  sun: { cx: 12, cy: 12, r: 4 },
};

export function icon(name: IconName, opts: IconOptions = {}): SVGSVGElement {
  const size = opts.size ?? 16;
  const stroke = opts.stroke ?? 2;
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", String(stroke));
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", opts.ariaLabel ? "false" : "true");
  if (opts.ariaLabel) svg.setAttribute("aria-label", opts.ariaLabel);
  svg.setAttribute(
    "class",
    opts.className ? `lib-icon ${opts.className}` : "lib-icon",
  );

  const circle = CIRCLE_ICONS[name];
  if (circle) {
    const c = document.createElementNS(NS, "circle");
    c.setAttribute("cx", String(circle.cx));
    c.setAttribute("cy", String(circle.cy));
    c.setAttribute("r", String(circle.r));
    svg.appendChild(c);
  }

  // Multi-path via " M "-Trennung
  const d = PATHS[name];
  const parts = d.split(/(?= M )/g).map((s) => s.trim());
  for (const p of parts) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", p);
    svg.appendChild(path);
  }

  return svg;
}

/**
 * Convenience: Button mit Icon + Label, mit konsistentem Spacing.
 */
export function iconButton(
  name: IconName,
  label: string,
  opts: IconOptions & { onClick?: () => void; href?: string } = {},
): HTMLElement {
  const el = opts.href
    ? document.createElement("a")
    : document.createElement("button");
  if (el.tagName === "A") {
    (el as HTMLAnchorElement).href = opts.href as string;
    (el as HTMLAnchorElement).target = "_blank";
    (el as HTMLAnchorElement).rel = "noopener noreferrer";
  } else {
    (el as HTMLButtonElement).type = "button";
  }
  // Optik + Hover in CSS (.icon-button) statt inline — Architekt #2.
  el.className = "icon-button";
  el.appendChild(icon(name, { size: opts.size ?? 14, stroke: opts.stroke }));
  const span = document.createElement("span");
  span.textContent = label;
  el.appendChild(span);
  if (opts.onClick) el.addEventListener("click", opts.onClick);
  return el;
}
